import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { retry, tap } from 'rxjs/operators';

import { DEFAULT_PRIMARY_COLOR } from '@plugins/newsfeed-lib/constants';
import type {
  BrandingSettingsDto,
  InstanceDto,
} from '@plugins/newsfeed-lib/instance';
import type { ChannelDto } from '@plugins/newsfeed-lib/types';

/** The one custom property the whole plugin themes itself from. */
const PRIMARY_COLOR_VARIABLE = '--newsfeed-primary-color';

const DEFAULT_SETTINGS: BrandingSettingsDto = {
  primaryColor: DEFAULT_PRIMARY_COLOR,
  logo: null,
};

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly http = inject(HttpClient);
  public instance!: InstanceDto;

  /**
   * The instance's channels, as a signal so the filter row and the badges
   * follow a refresh. They arrive with the instance payload rather than from
   * a call of their own, so the first paint needs no second round trip.
   */
  readonly channels = signal<ChannelDto[]>([]);

  readonly settings = signal<BrandingSettingsDto>(DEFAULT_SETTINGS);

  readonly primaryColor = computed(() => this.settings().primaryColor);

  readonly logo = computed(() => this.settings().logo);

  fetchInstance(): Observable<InstanceDto> {
    return this.http.get<InstanceDto>('/api/instance').pipe(
      retry({ delay: 7000 }),
      tap((instance) => this.apply(instance)),
    );
  }

  /**
   * Same payload, but failing fast instead of retrying forever: this is the
   * recovery path for a stale channel list, not app bootstrap, so a caller
   * needs to be able to give up.
   */
  refreshInstance(): Observable<InstanceDto> {
    return this.http
      .get<InstanceDto>('/api/instance')
      .pipe(tap((instance) => this.apply(instance)));
  }

  updateInstance(instance: Partial<InstanceDto>): Observable<InstanceDto> {
    return this.http
      .patch<InstanceDto>('/api/instance', { instance })
      .pipe(tap((updated) => this.apply(updated)));
  }

  private apply(instance: InstanceDto): void {
    this.instance = instance;
    this.channels.set(instance.channels ?? []);

    const settings: BrandingSettingsDto = {
      primaryColor: instance.settings?.primaryColor || DEFAULT_PRIMARY_COLOR,
      logo: instance.settings?.logo ?? null,
    };
    this.settings.set(settings);
    document.documentElement.style.setProperty(
      PRIMARY_COLOR_VARIABLE,
      settings.primaryColor,
    );
  }
}
